// src/components/royalty/RoyaltyDashboard.tsx
// ロイヤリティダッシュボード

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CurrencyYenIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  getReceivedRoyalties,
  getPaidRoyalties,
  getRoyaltySummary,
  formatRoyaltyAmount,
  formatRoyaltyDate,
  RoyaltyRecord,
  RoyaltySummaryResponse,
} from '../../services/royaltyService';

interface RoyaltyDashboardProps {
  className?: string;
  /** trueの場合、ロイヤリティ活動がなくても常に表示 */
  alwaysShow?: boolean;
  /** ユーザーがライセンスを有効にしたキャラクターを持っている場合true */
  hasLicensingEnabled?: boolean;
}

const RoyaltyDashboard: React.FC<RoyaltyDashboardProps> = ({
  className = '',
  alwaysShow = false,
  hasLicensingEnabled = false,
}) => {
  const [activeTab, setActiveTab] = useState<'received' | 'paid'>('received');
  const [summary, setSummary] = useState<RoyaltySummaryResponse | null>(null);
  const [receivedRoyalties, setReceivedRoyalties] = useState<RoyaltyRecord[]>([]);
  const [paidRoyalties, setPaidRoyalties] = useState<RoyaltyRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMoreReceived, setHasMoreReceived] = useState(false);
  const [hasMorePaid, setHasMorePaid] = useState(false);
  const [hasAnyActivity, setHasAnyActivity] = useState(false);

  // Fetch summary and initial data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [summaryData, receivedData, paidData] = await Promise.all([
          getRoyaltySummary(),
          getReceivedRoyalties(10),
          getPaidRoyalties(10),
        ]);

        setSummary(summaryData);
        setReceivedRoyalties(receivedData.royalties);
        setHasMoreReceived(receivedData.hasMore);
        setPaidRoyalties(paidData.royalties);
        setHasMorePaid(paidData.hasMore);

        // ロイヤリティ活動があるかチェック
        const hasActivity =
          summaryData.totalReceived > 0 ||
          summaryData.totalPaid > 0 ||
          summaryData.pendingReceived > 0 ||
          summaryData.pendingPaid > 0 ||
          summaryData.receivedCount > 0 ||
          summaryData.paidCount > 0;
        setHasAnyActivity(hasActivity);
      } catch (err) {
        console.error('Failed to fetch royalty data:', err);
        setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load more royalties
  const handleLoadMore = async () => {
    if (isLoadingMore) return;

    setIsLoadingMore(true);

    try {
      const currentList = activeTab === 'received' ? receivedRoyalties : paidRoyalties;
      const lastItem = currentList[currentList.length - 1];
      const before = lastItem?.createdAt;

      if (activeTab === 'received') {
        const data = await getReceivedRoyalties(10, before);
        setReceivedRoyalties((prev) => [...prev, ...data.royalties]);
        setHasMoreReceived(data.hasMore);
      } else {
        const data = await getPaidRoyalties(10, before);
        setPaidRoyalties((prev) => [...prev, ...data.royalties]);
        setHasMorePaid(data.hasMore);
      }
    } catch (err) {
      console.error('Failed to load more royalties:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400 text-[10px] rounded-full">
            <CheckCircleIcon className="w-3 h-3" />
            完了
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 text-[10px] rounded-full">
            <ClockIcon className="w-3 h-3" />
            処理中
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 text-[10px] rounded-full">
            <ExclamationTriangleIcon className="w-3 h-3" />
            失敗
          </span>
        );
      case 'simulated':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 text-[10px] rounded-full">
            テスト
          </span>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className={`p-6 ${className}`}>
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 ${className}`}>
        <div className="p-4 bg-red-50 dark:bg-red-500/20 rounded-xl text-red-600 dark:text-red-300 text-sm">
          {error}
        </div>
      </div>
    );
  }

  // ロイヤリティ活動がない場合は非表示
  // ただし以下の場合は表示:
  // - alwaysShow が true
  // - hasLicensingEnabled が true（ライセンスを有効にしているキャラがある）
  // - hasAnyActivity が true（ロイヤリティ活動がある）
  if (!alwaysShow && !hasLicensingEnabled && !hasAnyActivity) {
    return null;
  }

  const currentRoyalties = activeTab === 'received' ? receivedRoyalties : paidRoyalties;
  const hasMore = activeTab === 'received' ? hasMoreReceived : hasMorePaid;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        {/* Received */}
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-2xl p-4 border border-green-500/20">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-green-500/30 flex items-center justify-center">
              <ArrowDownIcon className="w-4 h-4 text-green-400" />
            </div>
            <span className="text-xs text-green-400/80">受け取り</span>
          </div>
          <p className="text-2xl font-bold text-green-400">
            {formatRoyaltyAmount(summary?.totalReceived || 0)}
          </p>
          <p className="text-[10px] text-gray-500 dark:text-white/40 mt-1">
            {summary?.receivedCount || 0}件
            {(summary?.pendingReceived || 0) > 0 && (
              <span className="text-yellow-600/80 dark:text-yellow-400/60 ml-1">
                (+{formatRoyaltyAmount(summary?.pendingReceived || 0)} 処理中)
              </span>
            )}
          </p>
        </div>

        {/* Paid */}
        <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-2xl p-4 border border-red-500/20">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-red-500/30 flex items-center justify-center">
              <ArrowUpIcon className="w-4 h-4 text-red-400" />
            </div>
            <span className="text-xs text-red-400/80">支払い</span>
          </div>
          <p className="text-2xl font-bold text-red-400">
            {formatRoyaltyAmount(summary?.totalPaid || 0)}
          </p>
          <p className="text-[10px] text-gray-500 dark:text-white/40 mt-1">
            {summary?.paidCount || 0}件
            {(summary?.pendingPaid || 0) > 0 && (
              <span className="text-yellow-600/80 dark:text-yellow-400/60 ml-1">
                (+{formatRoyaltyAmount(summary?.pendingPaid || 0)} 処理中)
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 p-1 bg-gray-100 dark:bg-white/5 rounded-xl">
        <button
          onClick={() => setActiveTab('received')}
          className={`flex-1 py-2 px-4 text-xs font-medium rounded-lg transition-colors ${
            activeTab === 'received'
              ? 'bg-green-100 dark:bg-green-500/30 text-green-600 dark:text-green-400'
              : 'text-gray-500 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/60'
          }`}
        >
          <ArrowDownIcon className="w-3 h-3 inline mr-1" />
          受け取り履歴
        </button>
        <button
          onClick={() => setActiveTab('paid')}
          className={`flex-1 py-2 px-4 text-xs font-medium rounded-lg transition-colors ${
            activeTab === 'paid'
              ? 'bg-red-100 dark:bg-red-500/30 text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/60'
          }`}
        >
          <ArrowUpIcon className="w-3 h-3 inline mr-1" />
          支払い履歴
        </button>
      </div>

      {/* Royalty List */}
      <div className="space-y-3">
        <AnimatePresence mode="wait">
          {currentRoyalties.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-8"
            >
              <CurrencyYenIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-white/20 mb-3" />
              <p className="text-sm text-gray-500 dark:text-white/40">
                {activeTab === 'received'
                  ? 'まだロイヤリティを受け取っていません'
                  : 'まだロイヤリティを支払っていません'}
              </p>
              {activeTab === 'received' && hasLicensingEnabled && (
                <p className="text-xs text-gray-400 dark:text-white/30 mt-2">
                  他のユーザーがあなたのキャラクターでグッズを販売すると、
                  <br />
                  ここにロイヤリティ収益が表示されます
                </p>
              )}
            </motion.div>
          ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-2"
            >
              {currentRoyalties.map((royalty) => (
                <div
                  key={royalty.id}
                  className="bg-white dark:bg-white/5 rounded-xl p-3 border border-gray-200 dark:border-white/10"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {royalty.characterName || 'キャラクター'}
                        </p>
                        {getStatusBadge(royalty.status)}
                      </div>
                      <p className="text-[10px] text-gray-500 dark:text-white/40">
                        {activeTab === 'received' ? (
                          <>From: {royalty.creatorName || '購入者'}</>
                        ) : (
                          <>To: {royalty.licensorName || 'ライセンサー'}</>
                        )}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-white/30">
                        {formatRoyaltyDate(royalty.createdAt)} · {royalty.royaltyPercentage}%
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={`text-lg font-bold ${
                          activeTab === 'received' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}
                      >
                        {activeTab === 'received' ? '+' : '-'}
                        {formatRoyaltyAmount(royalty.royaltyAmount)}
                      </p>
                      <p className="text-[9px] text-gray-400 dark:text-white/30">
                        注文金額: {formatRoyaltyAmount(royalty.orderAmount)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Load More */}
        {hasMore && (
          <button
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="w-full py-3 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-white/60 hover:text-gray-700 dark:hover:text-white/80 transition-colors"
          >
            {isLoadingMore ? (
              <div className="w-4 h-4 border-2 border-gray-300 dark:border-white/30 border-t-gray-600 dark:border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <ChevronDownIcon className="w-4 h-4" />
                もっと見る
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default RoyaltyDashboard;
